import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Gem, Sparkles, Gavel, Network, Landmark, ClipboardList,
  ShieldCheck, KeyRound, FileText, Newspaper, BellRing, Settings, LogOut, Menu, X, Bell,
  TrendingUp, Globe2, BookOpen, Archive, Crown, Gauge, Handshake, Diamond, ScrollText, Wallet, CalendarClock,
  Store, PackageCheck, Coins, Scale, FileCheck2, Fingerprint, Lock, CandlestickChart, Radar, Search,
} from "lucide-react";
import { TierBadge, CredibilityBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { useLaunchStatus, TeaserGate } from "@/components/LaunchGate";


const navLinkCls = (isActive, locked) => {
  if (isActive) return "border-bb-gold text-bb-gold opacity-100";
  if (locked) return "border-transparent text-bb-dim opacity-40 hover:opacity-70";
  return "border-transparent text-bb-text opacity-60 hover:opacity-100";
};

const SECTIONS = [
  ["Intelligence", [
    ["/dashboard", LayoutDashboard, "Dashboard", "explorer"],
    ["/dashboard/outlook", TrendingUp, "Market Outlook", "explorer"],
    ["/dashboard/brief", BookOpen, "Market Brief", "explorer"],
    ["/dashboard/verified-data", ShieldCheck, "Verified Data", "explorer"],
    ["/dashboard/indices", TrendingUp, "Price Indices", "explorer"],
    ["/dashboard/indices#rare-earths", Gem, "Rare Earths", "explorer"],
    ["/advisor", CandlestickChart, "Trading Advisor", "explorer"],
    ["/dashboard/signals", Radar, "Signal Intelligence", "explorer"],
    ["/dashboard/research", Search, "Research Tools", "explorer"],
    ["/dashboard/markets", Globe2, "African Markets", "explorer"],
    ["/dashboard/trade", CandlestickChart, "Virtus Trade", "explorer"],
    ["/dashboard/accuracy", Gauge, "Prediction Accuracy", "explorer"],
    ["/yield", Landmark, "Yield Desk", "explorer"],
    ["/dashboard/price-reference", Coins, "Price Reference", "explorer"],
    ["/dashboard/producers", Globe2, "Producer Atlas", "explorer"],
    ["/dashboard/passports", Fingerprint, "Provenance Passports", "explorer"],
    ["/dashboard/yield-model", Scale, "Yield & Sourcing", "trader"],
    ["/dashboard/compliance", ShieldCheck, "Shipping & Compliance", "explorer"],
    ["/dashboard/certificates", FileCheck2, "Certificate Vault", "explorer"],
    ["/dashboard/origin-premium", Crown, "Origin Premium", "institutional"],
    ["/dashboard/discipline-scorecard", Gauge, "Discipline Score", "institutional"],
    ["/dashboard/investment-stones", Diamond, "Investment Stones", "institutional"],
    ["/dashboard/fiscal-monitor", Wallet, "Fiscal Monitor", "institutional"],
    ["/dashboard/rough", Gem, "Rough Pricing", "explorer"],
    ["/dashboard/polished", Sparkles, "Polished Pricing", "trader"],
    ["/dashboard/auctions", Gavel, "Auction Events", "explorer"],
    ["/dashboard/archive", Archive, "Discipline Archive", "explorer"],
    ["/dashboard/odc", Network, "Supply Intelligence", "trader"],
    ["/dashboard/macro", Landmark, "Botswana Macro", "trader"],
  ]],
  ["Trade", [
    ["/dashboard/inventory", Store, "Available Inventory", "trader"],
    ["/dashboard/deal-room", Handshake, "Deal Room", "institutional"],
    ["/dashboard/my-orders", PackageCheck, "My Orders", "explorer"],
    ["/dashboard/orders/track", ClipboardList, "My Order Tracking", "institutional"],
    ["/dashboard/orders", ClipboardList, "Diamond Orders", "trader"],
    ["/dashboard/alerts", BellRing, "Price Alerts", "trader"],
  ]],
  ["Registry", [
    ["/dashboard/registry", ShieldCheck, "VDR Registry", "institutional"],
  ]],
  ["Reports", [
    ["/dashboard/strategic-briefs", ScrollText, "Strategic Briefs", "institutional"],
    ["/dashboard/weekly-brief", CalendarClock, "Weekly Brief", "institutional"],
    ["/dashboard/memos", FileText, "Monthly Memos", "explorer"],
    ["/dashboard/news", Newspaper, "Market News", "explorer"],
    ["/dashboard/api", KeyRound, "API Access", "institutional"],
  ]],
];

function nowGMT() {
  const d = new Date();
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}  ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} GMT`;
}

export default function DashboardLayout() {
  const { user, loading, logout, hasTier } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const launch = useLaunchStatus();

  useEffect(() => { if (!loading && !user) nav("/login"); }, [loading, user, nav]);
  if (loading || !user) return <div className="min-h-screen bg-bb-bg flex items-center justify-center text-bb-secondary">Loading</div>;
  const teaserGated = launch?.teaser_mode && user.role !== "admin";


  const SidebarInner = () => (
    <div className="flex flex-col h-full bg-bb-bg">
      <button onClick={() => nav("/dashboard")} className="px-6 pt-7 pb-6 text-left" data-testid="sidebar-brand">
        <div className="font-serif text-3xl text-bb-gold font-semibold leading-none">VDR</div>
        <div className="text-[0.6rem] uppercase tracking-[0.24em] text-bb-champagne mt-1.5">Virtus Diamond Registry</div>
      </button>
      <nav className="flex-1 overflow-y-auto pb-4">
        {SECTIONS.map(([label, items]) => (
          <div key={label} className="mb-5">
            <div className="px-6 mb-1.5 text-[0.6rem] uppercase tracking-[0.2em] text-bb-dim">{label}</div>
            {items.map(([to, Icon, name, minTier]) => {
              const locked = !hasTier(minTier);
              return (
                <NavLink key={to} to={to} end={to === "/dashboard"} onClick={() => setOpen(false)}
                  data-testid={`nav-${name.toLowerCase().replace(/ /g, "-")}`}
                  title={locked ? `Requires ${minTier.charAt(0).toUpperCase() + minTier.slice(1)} plan` : undefined}
                  className={({ isActive }) =>
                    `flex items-center gap-3 pl-6 pr-4 py-2 text-[0.8125rem] font-medium border-l-2 transition-opacity ${navLinkCls(isActive, locked)}`}>
                  <Icon size={15} className="shrink-0" />
                  <span className="flex-1">{name}</span>
                  {locked && <Lock size={11} className="text-bb-dim shrink-0" data-testid={`nav-lock-${name.toLowerCase().replace(/ /g, "-")}`} />}
                </NavLink>
              );
            })}
          </div>
        ))}
        {user.role === "admin" && (
          <div className="mb-3">
            <div className="px-6 mb-1.5 text-[0.6rem] uppercase tracking-[0.2em] text-bb-dim">Admin</div>
            <NavLink to="/dashboard/admin/revenue" onClick={() => setOpen(false)} data-testid="nav-revenue"
              className={({ isActive }) =>
                `flex items-center gap-3 pl-6 pr-4 py-2 text-[0.8125rem] font-medium border-l-2 transition-opacity ${
                  isActive ? "border-bb-gold text-bb-gold opacity-100" : "border-transparent text-bb-gold opacity-70 hover:opacity-100"}`}>
              <Wallet size={15} /> Revenue
            </NavLink>
            <NavLink to="/dashboard/admin/commerce" onClick={() => setOpen(false)} data-testid="nav-commerce-engine"
              className={({ isActive }) =>
                `flex items-center gap-3 pl-6 pr-4 py-2 text-[0.8125rem] font-medium border-l-2 transition-opacity ${
                  isActive ? "border-bb-gold text-bb-gold opacity-100" : "border-transparent text-bb-gold opacity-70 hover:opacity-100"}`}>
              <Coins size={15} /> Commerce Engine
            </NavLink>
            <NavLink to="/admin" onClick={() => setOpen(false)} data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 pl-6 pr-4 py-2 text-[0.8125rem] font-medium border-l-2 transition-opacity ${
                  isActive ? "border-bb-gold text-bb-gold opacity-100" : "border-transparent text-bb-gold opacity-70 hover:opacity-100"}`}>
              <Settings size={15} /> Admin Console
            </NavLink>
          </div>
        )}
      </nav>
      <div className="px-6 py-4 border-t border-bb-line">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-bb-text truncate">{user.full_name}</div>
          <TierBadge tier={user.membership_tier} role={user.role} />
        </div>
        <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn"
          className="flex items-center gap-2 text-[0.75rem] uppercase tracking-wider text-bb-dim hover:text-bb-text transition-colors">
          <LogOut size={13} /> Sign Out
        </button>
      </div>
    </div>
  );

  const crumb = loc.pathname.replace("/dashboard", "Dashboard").replace(/^\//, "").replace("/", " / ").replace(/-/g, " ") || "Dashboard";

  const CREDIBILITY_ROUTES = {
    live: ["/dashboard/verified-data", "/dashboard/producers", "/dashboard/news"],
    modeled: ["/dashboard/price-reference", "/dashboard/indices", "/dashboard/origin-premium",
      "/dashboard/discipline-scorecard", "/dashboard/fiscal-monitor", "/dashboard/weekly-brief",
      "/dashboard/strategic-briefs", "/dashboard/brief", "/dashboard/yield-model",
      "/dashboard/compliance"],
  };
  const pageCredibility = (() => {
    const p = loc.pathname;
    if (p.startsWith("/dashboard/certificates") || p.startsWith("/dashboard/passports")) return "live";
    if (CREDIBILITY_ROUTES.live.some((r) => p.startsWith(r))) return "live";
    if (CREDIBILITY_ROUTES.modeled.some((r) => p.startsWith(r))) return "modeled";
    return "illustrative";
  })();

  return (
    <div className="min-h-screen bg-bb-bg text-bb-text flex">
      <aside className="hidden lg:flex w-[220px] shrink-0 fixed inset-y-0">{SidebarInner()}</aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-[220px]">{SidebarInner()}</div>
          <div className="flex-1 bg-black/70" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-[220px] min-w-0">
        <header className="h-[52px] bg-bb-surface border-b border-bb-line flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-bb-text" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div className="text-[0.8rem] text-bb-secondary capitalize hidden sm:block">{crumb}</div>
            <CredibilityBadge status={pageCredibility} className="hidden sm:inline-flex" />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[0.75rem] text-bb-dim tnum hidden sm:block">{nowGMT()}</span>
            <Bell size={16} className="text-bb-secondary hover:text-bb-gold transition-colors cursor-pointer" />
            <div className="w-7 h-7 rounded-sm bg-bb-raised border border-bb-gold/30 flex items-center justify-center text-bb-gold text-xs font-serif">
              {user.full_name?.[0] || "B"}
            </div>
          </div>
        </header>

        {user.role !== "admin" && user.subscription_status !== "active" && (
          <div className="bg-bb-gold/[0.08] border-b border-bb-gold/20 px-6 py-2 flex flex-wrap items-center justify-between gap-2" data-testid="trial-banner">
            <span className="text-bb-gold text-xs">Trial access active. Activate your annual membership to keep uninterrupted access.</span>
            <button onClick={() => nav("/pricing")} data-testid="trial-activate-btn"
              className="bg-bb-gold text-bb-bg hover:bg-bb-champagne transition-colors rounded-sm px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-wide">
              Activate Membership
            </button>
          </div>
        )}

        <main className="p-8 max-w-[1280px] mx-auto bb-fade-up" key={loc.pathname}>
          {teaserGated ? <TeaserGate /> : <Outlet />}
          <div className="mt-12 pt-4 border-t border-white/5 text-bb-dim text-[0.64rem] leading-relaxed" data-testid="data-ownership-footer">
            VDR proprietary market intelligence, compiled with diamond industry knowledge capture.
            Not investment advice.
          </div>
        </main>
      </div>
    </div>
  );
}

